import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Link,
  Divider,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import type { Category } from '../../types/category';
import { categoryProductsApi } from '../../api/categoryProducts';

interface SubcategoryFilterSidebarProps {
  categories: Category[];
  selectedSubcategories: number[];
  selectedBrands?: number[];
  onSubcategoryChange: (subcategoryIds: number[]) => void;
  currentCategoryId: number;
  isMobile?: boolean;
}

const SubcategoryFilterSidebar: React.FC<SubcategoryFilterSidebarProps> = ({
  categories,
  selectedSubcategories,
  selectedBrands = [],
  onSubcategoryChange,
  currentCategoryId,
  isMobile = false,
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [subcategoryCounts, setSubcategoryCounts] = useState<Map<number, number>>(new Map());

  // Fetch counts from API for each subcategory
  useEffect(() => {
    const fetchSubcategoryCounts = async () => {
      const subcategories = categories.filter(
        category => category.parentId === currentCategoryId && category.isActive
      );

      if (subcategories.length === 0) {
        return;
      }

      const countsMap = new Map<number, number>();

      // Fetch count for each subcategory with categoryId + optional brandIds
      await Promise.all(
        subcategories.map(async (subcategory) => {
          try {
            const params = {
              categoryIds: [subcategory.id],
              brandIds: selectedBrands.length > 0 ? selectedBrands : undefined,
            };
            
            const result = await categoryProductsApi.getProductCount(params);
            countsMap.set(subcategory.id, result.count);
          } catch (error) {
            console.error(`Error fetching count for subcategory ${subcategory.id}:`, error);
            countsMap.set(subcategory.id, 0);
          }
        })
      );

      setSubcategoryCounts(countsMap);
    };

    fetchSubcategoryCounts();
  }, [categories, currentCategoryId, selectedBrands]);

  // Get subcategories for the current category with product counts
  const subcategoriesWithCounts = useMemo(() => {
    // Filter subcategories that belong to the current category
    const subcategories = categories
      .filter(category => category.parentId === currentCategoryId && category.isActive)
      .map(category => ({
        ...category,
        count: subcategoryCounts.get(category.id) || 0,
      }))
      .sort((a, b) => b.count - a.count); // Sort by count descending

    return subcategories;
  }, [categories, currentCategoryId, subcategoryCounts]);

  const visibleSubcategories = showAll ? subcategoriesWithCounts : subcategoriesWithCounts.slice(0, 8);

  const handleSubcategoryToggle = (subcategoryId: number) => {
    if (selectedSubcategories.includes(subcategoryId)) {
      onSubcategoryChange(selectedSubcategories.filter(id => id !== subcategoryId));
    } else {
      onSubcategoryChange([...selectedSubcategories, subcategoryId]);
    }
  };


  // Helper function to get category name based on language
  const getCategoryName = (category: Category, language: string) => {
    switch (language) {
      case 'zh':
        return category.nameZh || category.name;
      case 'cn':
        return category.nameCn || category.name;
      default:
        return category.name;
    }
  };

  // Don't render if no subcategories
  if (subcategoriesWithCounts.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'white',
        borderRadius: isMobile ? 0 : 2,
        p: 3,
        height: 'fit-content',
        ...(isMobile ? {} : { position: 'sticky', top: 100 }),
        mb: isMobile ? 0 : 2,
        border: isMobile ? 'none' : '1px solid #e0e0e0',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            color: '#333',
          }}
        >
          {t('category.subcategories')}
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {/* Divider */}
        <Divider sx={{ mb: 2 }} />
        

        {/* Subcategory List */}
        <Box sx={{ mt: 1 }}>
          {visibleSubcategories.map((subcategory) => (
            <FormControlLabel
              key={subcategory.id}
              control={
                <Checkbox
                  checked={selectedSubcategories.includes(subcategory.id)}
                  onChange={() => handleSubcategoryToggle(subcategory.id)}
                  sx={{
                    color: '#b48b6a',
                    '&.Mui-checked': {
                      color: '#b48b6a',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    {getCategoryName(subcategory, i18n.language)}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#666', ml: 1 }}>
                    ({subcategory.count})
                  </Typography>
                </Box>
              }
              sx={{ 
                width: '100%',
                mb: 0.5,
                '& .MuiFormControlLabel-label': {
                  width: '100%',
                },
              }}
            />
          ))}

          {/* Show More/Less */}
          {subcategoriesWithCounts.length > 8 && (
            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowAll(!showAll)}
                sx={{
                  color: '#b48b6a',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {showAll ? t('category.showLess') : `${t('category.showMore')} (${subcategoriesWithCounts.length - 8})`}
              </Link>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SubcategoryFilterSidebar;
